import React, { useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Inbox, ToggleLeft, Megaphone, FileText,
  Newspaper, MessageSquareQuote, Package as PkgIcon, ScrollText,
  ShieldCheck, HardDrive, LogOut, Users, UserCircle, Settings, Shield,
  Sparkles, GraduationCap, ChevronDown, Menu, X, ExternalLink,
} from "lucide-react";
import HawkamLogo from "@/components/HawkamLogo";

// Grouped navigation — every route below is an EXISTING admin route.
// Adding new modules (Regulatory countries/frameworks etc.) is deferred to
// a later section; only groups whose pages exist today are surfaced here.
const GROUPS = [
  {
    label: "Dashboard",
    items: [{ to: "/admin", label: "Overview", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Website Content",
    items: [
      { to: "/admin/sections", label: "Homepage Sections", icon: ToggleLeft },
      { to: "/admin/services", label: "Services", icon: Sparkles },
      { to: "/admin/packages", label: "Packages & Offers", icon: PkgIcon },
      { to: "/admin/news", label: "News", icon: Newspaper },
      { to: "/admin/articles", label: "Articles", icon: FileText },
      { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
      { to: "/admin/team", label: "Team", icon: Users },
    ],
  },
  {
    label: "Governance & Compliance",
    items: [
      { to: "/admin/governance", label: "Governance Assessment", icon: ShieldCheck },
      { to: "/admin/assessment", label: "Assessment Questions", icon: FileText },
      { to: "/admin/policies", label: "Governance Documents", icon: ScrollText },
    ],
  },
  {
    label: "Clients & Requests",
    items: [
      { to: "/admin/inbox", label: "Client Submissions", icon: Inbox },
      { to: "/admin/customers", label: "Customers", icon: Users },
    ],
  },
  {
    label: "Training",
    items: [{ to: "/admin/trainings", label: "Trainings", icon: GraduationCap }],
  },
  {
    label: "Administration",
    items: [
      { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
      { to: "/admin/users", label: "Users & Access", icon: UserCircle },
      { to: "/admin/settings", label: "Site Settings", icon: Settings },
      { to: "/admin/gdpr", label: "GDPR Console", icon: Shield },
      { to: "/admin/backups", label: "Backups", icon: HardDrive },
      { to: "/admin/audit-log", label: "Activity Log", icon: ScrollText },
      { to: "/admin/profile", label: "Profile & Security", icon: UserCircle },
    ],
  },
];

function NavGroup({ group, onNavigate }) {
  const loc = useLocation();
  const isGroupActive = group.items.some((it) =>
    it.end ? loc.pathname === it.to : loc.pathname.startsWith(it.to),
  );
  const [open, setOpen] = useState(isGroupActive || group.label === "Dashboard");

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-2 text-[10px] uppercase tracking-[0.24em] text-gold/80 hover:text-gold"
        data-testid={`admin-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <span>{group.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && group.items.map(({ to, label, icon: I, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors ${
              isActive ? "bg-navy-900 border-gold text-gold" : "border-transparent text-cream/80 hover:text-gold hover:bg-navy-900"
            }`
          }
        >
          <I size={15} />
          <span>{label}</span>
        </NavLink>
      ))}
    </div>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-cream text-navy/60">Loading…</div>;
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" state={{ from: loc }} replace />;
  }

  const doLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  const sidebar = (onNav) => (
    <>
      <div className="p-6 border-b border-gold/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HawkamLogo size={40} variant="mark" />
          <div>
            <div className="font-serif text-lg">HawKam</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin Console</div>
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden text-cream/70 hover:text-gold" aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {GROUPS.map((g) => <NavGroup key={g.label} group={g} onNavigate={onNav} />)}
      </nav>
      <div className="p-4 border-t border-gold/20">
        <div className="text-xs text-cream/60 mb-2 truncate">{user.email}</div>
        <button
          data-testid="admin-logout"
          onClick={doLogout}
          className="w-full flex items-center gap-2 text-cream/80 hover:text-gold text-sm"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-cream" dir="ltr" data-testid="admin-layout">
      {/* Desktop / tablet sidebar */}
      <aside className="hidden lg:flex w-64 bg-navy text-cream flex-col sticky top-0 h-screen">
        {sidebar(null)}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" />
          <aside className="relative w-72 max-w-[85vw] h-full bg-navy text-cream flex flex-col" onClick={(e) => e.stopPropagation()}>
            {sidebar(() => setMobileOpen(false))}
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        {/* Desktop top bar — links back to the public site */}
        <div className="hidden lg:flex items-center justify-between px-8 py-3 bg-white border-b border-navy/10 sticky top-0 z-30" data-testid="admin-topbar">
          <div className="flex items-center gap-4 text-xs">
            {[
              { to: "/", label: "Site" },
              { to: "/about", label: "About" },
              { to: "/services", label: "Services" },
              { to: "/articles", label: "Knowledge Library" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <Link key={l.to} to={l.to} target="_blank" rel="noopener noreferrer"
                    className="text-navy/70 hover:text-gold uppercase tracking-widest inline-flex items-center gap-1"
                    data-testid={`admin-topnav-${l.to.replace("/","") || "home"}`}>
                {l.label}
              </Link>
            ))}
          </div>
          <Link to="/" target="_blank" rel="noopener noreferrer"
                className="text-xs text-navy/60 hover:text-gold inline-flex items-center gap-1"
                data-testid="admin-view-site">
            <ExternalLink size={12} /> View public site
          </Link>
        </div>

        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-navy/10 bg-white">
          <button onClick={() => setMobileOpen(true)} className="text-navy" aria-label="Open menu" data-testid="admin-mobile-menu">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <HawkamLogo size={28} variant="mark" />
            <span className="font-serif text-navy">HawKam</span>
          </div>
          <Link to="/" target="_blank" rel="noopener noreferrer"
                className="text-navy/70 hover:text-gold" data-testid="admin-view-site-mobile"
                aria-label="View public site">
            <ExternalLink size={18} />
          </Link>
        </div>
        <div className="p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
